import {useState, useEffect} from 'react';

/**
 * Detects when the user has scrolled past a hero element using IntersectionObserver.
 * Zero scroll-event polling — fires only when the element crosses the viewport boundary.
 *
 * @param {React.RefObject} ref - Ref attached to the hero wrapper element to observe
 * @param {Object} [options]
 * @param {boolean} [options.enabled=false] - Only observe when true; SSR-safe (no-op on server)
 * @returns {{isPastCTAs: boolean}} isPastCTAs — true once the hero has left the viewport
 *
 * @example
 * const heroRef = useRef(null);
 * const {isPastCTAs} = useRangeScroll(heroRef, {enabled: !!enableScrollEffect});
 */
export const useRangeScroll = (ref, {enabled = false} = {}) => {
  const [isPastCTAs, setIsPastCTAs] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setIsPastCTAs(entry.boundingClientRect.bottom < 0);
        } else {
          setIsPastCTAs(false);
        }
      },
      {threshold: 0, rootMargin: '0px 0px -60px 0px'}
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, ref]);

  return {isPastCTAs};
};
